/**
 * Currency context (Faza 4) — resolves the viewer's display currency once and
 * exposes convert + format helpers. All amounts passed in are BASE ZAR; the
 * helpers convert to the resolved currency (ZAR or derived USD) and format via
 * the Intl-based formatter in src/lib/currency.ts.
 *
 * Mounted in RootLayout under UserPreferencesProvider + I18nextProvider.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { formatCurrency } from '@/lib/currency';
import {
  type RegionCurrency,
  USD_PER_ZAR,
  convertFromBaseZar,
  convertToBaseZar,
  resolveRegionCurrency,
} from '@/config/currency-region';

interface CurrencyContextValue {
  currency: RegionCurrency;
  fxRate: number; // USD per ZAR
  /** Convert a base-ZAR amount to the display currency (number). */
  convert: (amountZar: number) => number;
  /** Convert a base-ZAR amount and format it in the display currency. */
  format: (amountZar: number, opts?: { maximumFractionDigits?: number }) => string;
  /** Convert a display-currency amount back to base ZAR. */
  toBaseZar: (amount: number) => number;
}

const DEFAULT: CurrencyContextValue = {
  currency: 'ZAR',
  fxRate: USD_PER_ZAR,
  convert: (a) => a,
  format: (a) => formatCurrency(a, 'ZAR', 'en'),
  toBaseZar: (a) => a,
};

const CurrencyContext = createContext<CurrencyContextValue>(DEFAULT);

/**
 * `fallback` is what an undecided visitor sees. The planner sells worldwide and
 * falls back to its base currency; the Todae storefront is a South African
 * shop and mounts a nested provider with fallback="ZAR" — an explicit
 * preference or region still wins, in either.
 */
export function CurrencyProvider({ children, fallback, currency: forced }: { children: ReactNode; fallback?: RegionCurrency; currency?: RegionCurrency }) {
  const { preferences } = useUserPreferences();
  const { i18n } = useTranslation();

  const value = useMemo<CurrencyContextValue>(() => {
    // `currency` (forced) is for a nested provider that owns its own choice —
    // the Todae storefront — and must not inherit the planner's preference.
    const currency = forced ?? resolveRegionCurrency({
      region: preferences?.complianceRegion,
      currencyPref: preferences?.defaultCurrency,
      language: i18n?.language,
      fallback,
    });
    const locale = i18n?.language || 'en';
    return {
      currency,
      fxRate: USD_PER_ZAR,
      convert: (amountZar) => convertFromBaseZar(amountZar, currency),
      format: (amountZar, opts) =>
        formatCurrency(convertFromBaseZar(amountZar, currency), currency, locale),
      toBaseZar: (amount) => convertToBaseZar(amount, currency),
    };
  }, [preferences?.complianceRegion, preferences?.defaultCurrency, i18n?.language, fallback, forced]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useCurrency = () => useContext(CurrencyContext);
